// Helper functions for bill printing - Simple Design with Two Functions
// High-Contrast Black & White version for dark, sharp printing
// WITH FINE PAYMENT SUPPORT

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const cleanDate = dateString.replace(/^'/, "");
  return cleanDate;
};

export const generateTransactionBillHTML = (transaction, customers) => {
  const customer = customers.find((c) => c.id === transaction.customer) || {};
  const isGst = transaction.transactionDetails?.gstApplicable;

  const description =
    transaction.transactionDetails?.description ||
    transaction.description ||
    "";

  // ============ EXTRACT FINE DATA (same fallback chain used across the app) ============
  const fineRequired =
    transaction.transactionDetails?.finePayment?.requiredFine ||
    transaction.summary?.fineRequired ||
    transaction.paymentDetails?.finePayment?.totalFineRequired ||
    0;

  const finePaid =
    transaction.transactionDetails?.finePayment?.totalFinePaid ||
    transaction.summary?.finePaid ||
    transaction.paymentDetails?.finePayment?.totalFinePaid ||
    0;

  const fineBalance =
    transaction.transactionDetails?.finePayment?.balanceFine ??
    transaction.summary?.fineBalance ??
    transaction.paymentDetails?.finePayment?.balanceFine ??
    0;

  // Collect fine entries from every possible source and dedupe
  const paymentHistoryFines = (transaction.paymentDetails?.paymentHistory || []).flatMap(
    (p) => {
      const fines = [];
      if (p.paymentModes?.fine?.weight) {
        fines.push({
          metal: p.paymentModes.fine.metal || "silver",
          weight: parseFloat(p.paymentModes.fine.weight) || 0,
          purity: parseFloat(p.paymentModes.fine.purity) || 0,
        });
      }
      if (Array.isArray(p.paymentModes?.fineEntries)) {
        p.paymentModes.fineEntries.forEach((f) => {
          if (f.weight) {
            fines.push({
              metal: f.metal || "silver",
              weight: parseFloat(f.weight) || 0,
              purity: parseFloat(f.purity) || 0,
            });
          }
        });
      }
      return fines;
    }
  );

  const combinedFineEntries = [
    ...(transaction.paymentDetails?.finePayment?.entries || []),
    ...(transaction.transactionDetails?.finePayment?.entries || []),
    ...paymentHistoryFines,
  ].map((e) => ({
    metal: e.metal || "silver",
    weight: parseFloat(e.weight) || 0,
    purity: parseFloat(e.purity) || 0,
  }));

  const fineEntries = combinedFineEntries.filter((entry, index, arr) => {
    return (
      arr.findIndex(
        (e) =>
          e.metal === entry.metal &&
          e.weight === entry.weight &&
          e.purity === entry.purity
      ) === index
    );
  });

  const fineData = {
    required: parseFloat(fineRequired) || 0,
    paid: parseFloat(finePaid) || 0,
    balance: parseFloat(fineBalance) || 0,
    entries: fineEntries,
    hasFine: fineRequired > 0 || finePaid > 0,
  };

  const billData = {
    type: isGst ? "gst" : "cash",
    data: {
      transactionDetails: {
        billNo: transaction.transactionDetails?.billNo || 1,
        gstApplicable: isGst,
      },
      date: transaction.date,
      description: description,
      calculatedTotals: {
        subtotal: transaction.calculatedTotals?.subtotal || 0,
      },
      paymentDetails: {
        cash: transaction.paymentDetails?.cash || 0,
        online: transaction.paymentDetails?.online || 0,
        cheque: transaction.paymentDetails?.cheque || 0,
        other: transaction.paymentDetails?.other || 0,
        advancePayment: {
          enabled: transaction.paymentDetails?.advancePayment?.enabled || false,
          total: transaction.paymentDetails?.advancePayment?.total || 0,
          breakup: transaction.paymentDetails?.advancePayment?.breakup || {},
        },
        discount: transaction.paymentDetails?.discount || 0,
      },
      metaDetails: {
        handledBy: transaction.metaDetails?.handledBy || "",
        gstNumber: transaction.metaDetails?.gstNumber || "",
        panNumber: transaction.metaDetails?.panNumber || "",
      },
      summary: {
        grandTotal: transaction.summary?.grandTotal || 0,
        pendingAmount: transaction.summary?.pendingAmount || 0,
        cgst: transaction.summary?.cgst || 0,
        sgst: transaction.summary?.sgst || 0,
      },
      // NEW: pass fineData through so it's available to both bill generators
      fineData: fineData,
    },
    items: transaction.items || [],
    payments: transaction.paymentDetails || {},
    customer: customer,
    advanceData: transaction.paymentDetails?.advancePayment?.breakup || {},
  };
  return generateBillHTML(billData);
};

export const parseItemObject = (itemObject) => {
  if (!itemObject) return {};
  try {
    if (typeof itemObject === "string") {
      return JSON.parse(decodeURIComponent(itemObject));
    }
    return itemObject;
  } catch (error) {
    console.warn("Could not parse item object:", error);
    return {};
  }
};

export const getCarat = (melting) => {
  if (!melting) return "";
  switch (String(melting)) {
    case "92": return "22ct";
    case "75": return "18ct";
    case "76": return "18ct";
    case "58.5": return "14ct";
    case "50": return "12ct";
    case "999": return "24ct";
    default: return "";
  }
};

export const numberToWords = (num) => {
  if (typeof num !== "number") num = parseFloat(num) || 0;
  num = Math.floor(num);
  if (num === 0) return "Zero Rupees Only";

  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanOneThousand = (n) => {
    if (n === 0) return "";
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };

  const convertToIndianWords = (n) => {
    if (n === 0) return "";
    let crore = Math.floor(n / 10000000);
    n %= 10000000;
    let lakh = Math.floor(n / 100000);
    n %= 100000;
    let thousand = Math.floor(n / 1000);
    n %= 1000;
    let hundred = n;
    let result = "";
    if (crore > 0) result += convertLessThanOneThousand(crore) + " Crore ";
    if (lakh > 0) result += convertLessThanOneThousand(lakh) + " Lakh ";
    if (thousand > 0) result += convertLessThanOneThousand(thousand) + " Thousand ";
    if (hundred > 0) result += convertLessThanOneThousand(hundred);
    return result.trim();
  };

  const words = convertToIndianWords(num);
  return words ? words + " Rupees Only" : "Zero Rupees Only";
};

// Helper function to check if item is MRP
const isMRPItem = (item) => {
  const weight = parseFloat(item.weight) || 0;
  const grossWeight = parseFloat(item.grossWeight) || 0;
  const price = parseFloat(item.price) || 0;
  return (weight === 0 && grossWeight === 0 && price > 0);
};

// ============================================================
// SHARED ITEM ROWS BUILDER (used by both Cash Memo & GST Invoice)
// ============================================================
const buildItemsHTML = (items) => {
  let itemsHTML = "";
  let totalAmount = 0;
  let totalNetWeight = 0;

  items.forEach((item, index) => {
    const isMRP = isMRPItem(item);
    const weight = parseFloat(item.weight) || 0;
    const amount = parseFloat(item.price) || 0;
    const purity = item.melting || item.ct || "";

    let weightDisplay = "";
    let labourDisplay = "";

    if (isMRP) {
      weightDisplay = "1 Pcs";
      const labourValue = parseFloat(item.makingCharges || 0);
      labourDisplay = labourValue.toFixed(2);
    } else {
      weightDisplay = weight.toFixed(3);
      labourDisplay = parseFloat(item.makingCharges || 0).toFixed(2);
      totalNetWeight += weight;
    }

    totalAmount += amount;

    itemsHTML += `
      <tr>
        <td style="padding: 5px 6px; text-align: center; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${index + 1}</td>
        <td style="padding: 5px 6px; text-align: left; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${item.name || ""} ${isMRP ? '(MRP)' : ''}</td>
        <td style="padding: 5px 6px; text-align: center; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${purity}</td>
        <td style="padding: 5px 6px; text-align: center; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${weightDisplay}</td>
        <td style="padding: 5px 6px; text-align: center; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${parseFloat(item.metalRate || 0).toFixed(2)}</td>
        <td style="padding: 5px 6px; text-align: center; border: 2px solid #000000; font-size: 11px; font-weight: bold; color: #000000;">${labourDisplay}</td>
        <td style="padding: 5px 6px; text-align: right; border: 2px solid #000000; font-size: 11px; font-weight: 900; color: #000000;">₹ ${amount.toFixed(2)}</td>
      </tr>
    `;
  });

  // Add empty rows for consistent height (min 8 rows)
  const currentRowCount = items.length;
  const minTotalRows = 8;
  const rowsNeeded = Math.max(0, minTotalRows - currentRowCount);

  for (let i = 0; i < rowsNeeded; i++) {
    itemsHTML += `
      <tr>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
        <td style="padding: 5px 6px; border: 2px solid #000000; font-size: 11px; color: #000000;">&nbsp;</td>
      </tr>`;
  }

  return { itemsHTML, totalAmount, totalNetWeight };
};

// ============================================================
// SHARED ITEMS TABLE HEADER
// ============================================================
const buildItemsTable = (itemsHTML) => `
  <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px;">
    <thead>
      <tr style="border: 3px solid #000000; background: #ffffff;">
        <th style="padding: 6px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">#</th>
        <th style="padding: 6px; text-align: left; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Item</th>
        <th style="padding: 6px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Purity</th>
        <th style="padding: 6px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Wt / Pcs</th>
        <th style="padding: 6px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Rate</th>
        <th style="padding: 6px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Labour</th>
        <th style="padding: 6px; text-align: right; border: 2px solid #000000; font-weight: 900; font-size: 10px; color: #000000; background: #ffffff;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemsHTML}
    </tbody>
  </table>
`;

// ============================================================
// SHARED PAYMENT DETAILS SECTION
// ============================================================
const buildPaymentDetails = (payments, data) => `
  <div style="border: 3px solid #000000; margin-bottom: 8px; font-size: 11px;">
    <div style="display: grid; grid-template-columns: repeat(6, 1fr); text-align: center; border-bottom: 3px solid #000000; background: #ffffff;">
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Cash</div>
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Online</div>
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Cheque</div>
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Old Gold</div>
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Advance</div>
      <div style="padding: 7px 0; font-weight: 900; background: #ffffff; color: #000000;">Balance</div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(6, 1fr); text-align: center; font-weight: 900; color: #000000; background: #ffffff;">
      <div style="padding: 7px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(payments?.cash || 0).toFixed(2)}</div>
      <div style="padding: 7px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(payments?.online || 0).toFixed(2)}</div>
      <div style="padding: 7px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(payments?.cheque || 0).toFixed(2)}</div>
      <div style="padding: 7px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(payments?.other || 0).toFixed(2)}</div>
      <div style="padding: 7px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(payments?.advancePayment?.total || 0).toFixed(2)}</div>
      <div style="padding: 7px 0; font-weight: 900; color: #000000;">₹ ${parseFloat(data?.summary?.pendingAmount || 0).toFixed(2)}</div>
    </div>
  </div>
`;

// ============================================================
// SHARED ADVANCE BREAKUP SECTION
// ============================================================
const buildAdvanceBreakup = (payments, advanceData) => {
  if (!payments?.advancePayment?.enabled) return "";
  return `
    <div style="border: 3px solid #000000; margin-bottom: 8px; font-size: 10px;">
      <div style="display: grid; grid-template-columns: repeat(5, 1fr); text-align: center; border-bottom: 3px solid #000000; background: #ffffff;">
        <div style="padding: 6px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Adv Cash</div>
        <div style="padding: 6px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Adv Online</div>
        <div style="padding: 6px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Adv Gold</div>
        <div style="padding: 6px 0; font-weight: 900; background: #ffffff; color: #000000; border-right: 2px solid #000000;">Adv Silver</div>
        <div style="padding: 6px 0; font-weight: 900; background: #ffffff; color: #000000;">Other</div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(5, 1fr); text-align: center; font-weight: 900; color: #000000; background: #ffffff;">
        <div style="padding: 6px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(advanceData?.cash || 0).toFixed(2)}</div>
        <div style="padding: 6px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(advanceData?.online || 0).toFixed(2)}</div>
        <div style="padding: 6px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(advanceData?.oldGold || 0).toFixed(2)}</div>
        <div style="padding: 6px 0; font-weight: 900; border-right: 2px solid #000000; color: #000000;">₹ ${(advanceData?.oldSilver || 0).toFixed(2)}</div>
        <div style="padding: 6px 0; font-weight: 900; color: #000000;">₹ ${(advanceData?.custom || 0).toFixed(2)}</div>
      </div>
    </div>
  `;
};

// ============================================================
// NEW: FINE PAYMENT SECTION (renders only when hasFine = true)
// ============================================================
const buildFineSection = (fineData) => {
  if (!fineData || !fineData.hasFine) return "";

  const { required, paid, balance, entries } = fineData;

  // Build fine entries rows
  let entriesHTML = "";
  if (entries && entries.length > 0) {
    entries.forEach((entry, idx) => {
      const entryFine =
        (parseFloat(entry.weight) || 0) * (parseFloat(entry.purity) || 0) / 100;
      entriesHTML += `
        <tr>
          <td style="padding: 5px 8px; text-align: center; border: 2px solid #000000; font-size: 10px; font-weight: bold; color: #000000;">${idx + 1}</td>
          <td style="padding: 5px 8px; text-align: center; border: 2px solid #000000; font-size: 10px; font-weight: bold; color: #000000; text-transform: capitalize;">${entry.metal || "silver"}</td>
          <td style="padding: 5px 8px; text-align: center; border: 2px solid #000000; font-size: 10px; font-weight: bold; color: #000000;">${(parseFloat(entry.weight) || 0).toFixed(3)} g</td>
          <td style="padding: 5px 8px; text-align: center; border: 2px solid #000000; font-size: 10px; font-weight: bold; color: #000000;">${(parseFloat(entry.purity) || 0).toFixed(1)} %</td>
          <td style="padding: 5px 8px; text-align: right; border: 2px solid #000000; font-size: 10px; font-weight: 900; color: #000000;">${entryFine.toFixed(3)} g</td>
        </tr>
      `;
    });
  } else {
    entriesHTML = `
      <tr>
        <td colspan="5" style="padding: 6px; text-align: center; border: 2px solid #000000; font-size: 10px; color: #000000; font-style: italic;">
          No metal pieces recorded yet.
        </td>
      </tr>
    `;
  }

  return `
    <div style="border: 3px solid #000000; margin-bottom: 8px; font-size: 11px; background: #ffffff;">
      <!-- Section Header -->
      <div style="padding: 7px 12px; border-bottom: 3px solid #000000; background: #ffffff; font-weight: 900; font-size: 12px; color: #000000; letter-spacing: 1px; text-align: center;">
        ◆ FINE (METAL) PAYMENT ◆
      </div>

      <!-- Summary Row -->
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); text-align: center; border-bottom: 3px solid #000000; background: #ffffff;">
        <div style="padding: 6px 0; font-weight: 900; font-size: 10px; color: #000000; border-right: 2px solid #000000;">Fine Required</div>
        <div style="padding: 6px 0; font-weight: 900; font-size: 10px; color: #000000; border-right: 2px solid #000000;">Fine Paid</div>
        <div style="padding: 6px 0; font-weight: 900; font-size: 10px; color: #000000;">Fine Balance</div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); text-align: center; font-weight: 900; color: #000000; background: #ffffff; border-bottom: 3px solid #000000;">
        <div style="padding: 7px 0; font-weight: 900; font-size: 12px; border-right: 2px solid #000000; color: #000000;">${required.toFixed(3)} g</div>
        <div style="padding: 7px 0; font-weight: 900; font-size: 12px; border-right: 2px solid #000000; color: #000000;">${paid.toFixed(3)} g</div>
        <div style="padding: 7px 0; font-weight: 900; font-size: 12px; color: #000000;">${balance.toFixed(3)} g</div>
      </div>

      <!-- Metal Pieces Table -->
      <table style="width: 100%; font-size: 10px;">
        <thead>
          <tr style="border-bottom: 2px solid #000000; background: #ffffff;">
            <th style="padding: 5px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 9px; color: #000000; width: 8%;">#</th>
            <th style="padding: 5px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 9px; color: #000000; width: 22%;">Metal</th>
            <th style="padding: 5px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 9px; color: #000000; width: 22%;">Weight</th>
            <th style="padding: 5px; text-align: center; border: 2px solid #000000; font-weight: 900; font-size: 9px; color: #000000; width: 22%;">Purity</th>
            <th style="padding: 5px; text-align: right; border: 2px solid #000000; font-weight: 900; font-size: 9px; color: #000000; width: 26%;">Fine Weight</th>
          </tr>
        </thead>
        <tbody>
          ${entriesHTML}
        </tbody>
      </table>
    </div>
  `;
};

// ============================================================
// FUNCTION 1: CASH MEMO (Non-GST) - High Contrast B&W
// ============================================================
const generateCashMemo = (data, items, payments, customer, advanceData) => {
  const { itemsHTML, totalAmount, totalNetWeight } = buildItemsHTML(items);

  const discount = parseFloat(payments?.discount || 0);
  const grandTotal = totalAmount - discount;
  const amountWords = numberToWords(grandTotal).toUpperCase();
  const fineData = data.fineData;

  return `
    <div id="billContent" style="font-family: 'Courier New', monospace; width: 680px; margin: 0 auto; background: #ffffff; color: #000000; padding: 15px 20px; border: 4px solid #000000; box-sizing: border-box; font-size: 12px;">
      
      <!-- HEADER -->
      <div style="text-align: center; border-bottom: 4px solid #000000; padding-bottom: 8px; margin-bottom: 10px;">
        <div style="font-size: 26px; font-weight: 900; text-transform: uppercase; letter-spacing: 4px; color: #000000;">AR SILVER</div>
      </div>
      
      <!-- TITLE -->
      <div style="text-align: center; border-bottom: 4px solid #000000; padding: 8px 0; margin-bottom: 10px;">
        <div style="font-size: 22px; font-weight: 900; letter-spacing: 6px; color: #000000;">ESTIMATE</div>
      </div>
      
      <!-- CUSTOMER & BILL INFO -->
      <div style="border-bottom: 3px solid #000000; padding-bottom: 8px; margin-bottom: 10px; font-size: 12px; color: #000000;">
        <div style="display: flex; justify-content: space-between;">
          <div style="line-height: 1.9; font-weight: bold;">
            <div><strong style="font-weight: 900;">Customer:</strong> ${customer?.name || ""}</div>
            <div><strong style="font-weight: 900;">Address:</strong> ${customer?.address || ""}</div>
            <div><strong style="font-weight: 900;">Phone No.:</strong> ${customer?.contact || ""}</div>
          </div>
          <div style="text-align: right; line-height: 1.9; font-weight: bold;">
            <div><strong style="font-weight: 900;">Bill No:</strong> ${data.transactionDetails?.billNo || 1}</div>
            <div><strong style="font-weight: 900;">Date:</strong> ${formatDate(data.date)}</div>
          </div>
        </div>
      </div>
      
      <!-- ITEMS TABLE -->
      ${buildItemsTable(itemsHTML)}
      
      <!-- TOTALS -->
      <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px;">
        <div style="display: flex; justify-content: flex-end;">
          <div style="width: 260px; font-size: 12px; line-height: 2; color: #000000;">
            <div style="display: flex; justify-content: space-between; padding: 3px 0; font-weight: bold;">
              <span><strong style="font-weight: 900;">Subtotal</strong></span>
              <span><strong style="font-weight: 900;">₹ ${totalAmount.toFixed(2)}</strong></span>
            </div>
            ${discount > 0 ? `
              <div style="display: flex; justify-content: space-between; padding: 3px 0; border-top: 2px solid #000000; font-weight: bold;">
                <span>Discount</span>
                <span>− ₹ ${discount.toFixed(2)}</span>
              </div>
            ` : ""}
            <div style="display: flex; justify-content: space-between; border-top: 4px solid #000000; padding: 6px 0; font-weight: 900; font-size: 16px; margin-top: 2px; color: #000000;">
              <span>GRAND TOTAL</span>
              <span>₹ ${grandTotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; border-top: 2px solid #000000; padding: 5px 0; font-weight: 900; font-size: 13px; color: #000000;">
              <span>TOTAL NET WEIGHT</span>
              <span>${totalNetWeight.toFixed(3)} g</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- AMOUNT IN WORDS -->
      <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px; font-size: 12px; background: #ffffff; font-weight: 900; color: #000000;">
        <strong style="font-weight: 900;">In Words:</strong> ${amountWords}
      </div>
      
      <!-- PAYMENT DETAILS -->
      ${buildPaymentDetails(payments, data)}
      
      <!-- ADVANCE BREAKUP (if enabled) -->
      ${buildAdvanceBreakup(payments, advanceData)}

      <!-- FINE PAYMENT SECTION (only if fine exists) -->
      ${buildFineSection(fineData)}

      ${data.description ? `
        <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px; font-size: 12px; background: #ffffff; font-weight: 900; color: #000000;">
          <strong style="font-weight: 900;">Description:</strong> ${data.description}
        </div>
      ` : ""}
      
    </div>
  `;
};

// ============================================================
// FUNCTION 2: GST INVOICE - High Contrast B&W
// ============================================================
const generateGSTInvoice = (data, items, payments, customer, advanceData) => {
  const { itemsHTML, totalAmount, totalNetWeight } = buildItemsHTML(items);

  // Calculate GST
  const cgst = totalAmount * 0.015;
  const sgst = totalAmount * 0.015;
  const discount = parseFloat(payments?.discount || 0);
  const subtotalWithTax = totalAmount + cgst + sgst;
  const grandTotal = subtotalWithTax - discount;
  const amountWords = numberToWords(grandTotal).toUpperCase();
  const fineData = data.fineData;

  return `
    <div id="billContent" style="font-family: 'Courier New', monospace; width: 680px; margin: 0 auto; background: #ffffff; color: #000000; padding: 15px 20px; border: 4px solid #000000; box-sizing: border-box; font-size: 12px;">
      
      <!-- HEADER -->
      <div style="text-align: center; border-bottom: 4px solid #000000; padding-bottom: 8px; margin-bottom: 10px;">
        <div style="font-size: 26px; font-weight: 900; text-transform: uppercase; letter-spacing: 4px; color: #000000;">AR SILVER</div>
      </div>
      
      <!-- TITLE -->
      <div style="text-align: center; border-bottom: 4px solid #000000; padding: 8px 0; margin-bottom: 10px;">
        <div style="font-size: 22px; font-weight: 900; letter-spacing: 6px; color: #000000;">TAX INVOICE</div>
      </div>
      
      <!-- CUSTOMER & BILL INFO -->
      <div style="border-bottom: 3px solid #000000; padding-bottom: 8px; margin-bottom: 10px; font-size: 12px; color: #000000;">
        <div style="display: flex; justify-content: space-between;">
          <div style="line-height: 1.9; font-weight: bold;">
            <div><strong style="font-weight: 900;">Customer:</strong> ${customer?.name || ""}</div>
            <div><strong style="font-weight: 900;">Address:</strong> ${customer?.address || ""}</div>
            <div><strong style="font-weight: 900;">Phone No.:</strong> ${customer?.contact || ""}</div>
            ${data.metaDetails?.gstNumber ? `<div><strong style="font-weight: 900;">GST:</strong> ${data.metaDetails.gstNumber}</div>` : ""}
          </div>
          <div style="text-align: right; line-height: 1.9; font-weight: bold;">
            <div><strong style="font-weight: 900;">Bill No:</strong> ${data.transactionDetails?.billNo || 1}</div>
            <div><strong style="font-weight: 900;">Date:</strong> ${formatDate(data.date)}</div>
            <div><strong style="font-weight: 900;">Seller:</strong> ${data.metaDetails?.handledBy || ""}</div>
          </div>
        </div>
      </div>
      
      <!-- ITEMS TABLE -->
      ${buildItemsTable(itemsHTML)}
      
      <!-- TOTALS -->
      <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px;">
        <div style="display: flex; justify-content: flex-end;">
          <div style="width: 260px; font-size: 12px; line-height: 2; color: #000000;">
            <div style="display: flex; justify-content: space-between; padding: 3px 0; font-weight: bold;">
              <span><strong style="font-weight: 900;">Subtotal</strong></span>
              <span><strong style="font-weight: 900;">₹ ${totalAmount.toFixed(2)}</strong></span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 3px 0; border-top: 2px solid #000000; font-weight: bold;">
              <span>CGST (1.5%)</span>
              <span>₹ ${cgst.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 3px 0; font-weight: bold;">
              <span>SGST (1.5%)</span>
              <span>₹ ${sgst.toFixed(2)}</span>
            </div>
            ${discount > 0 ? `
              <div style="display: flex; justify-content: space-between; padding: 3px 0; border-top: 2px solid #000000; font-weight: bold;">
                <span>Discount</span>
                <span>− ₹ ${discount.toFixed(2)}</span>
              </div>
            ` : ""}
            <div style="display: flex; justify-content: space-between; border-top: 4px solid #000000; padding: 6px 0; font-weight: 900; font-size: 16px; margin-top: 2px; color: #000000;">
              <span>GRAND TOTAL</span>
              <span>₹ ${grandTotal.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; border-top: 2px solid #000000; padding: 5px 0; font-weight: 900; font-size: 13px; color: #000000;">
              <span>TOTAL NET WEIGHT</span>
              <span>${totalNetWeight.toFixed(3)} g</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- AMOUNT IN WORDS -->
      <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px; font-size: 12px; background: #ffffff; font-weight: 900; color: #000000;">
        <strong style="font-weight: 900;">In Words:</strong> ${amountWords}
      </div>
      
      <!-- PAYMENT DETAILS -->
      ${buildPaymentDetails(payments, data)}
      
      <!-- ADVANCE BREAKUP (if enabled) -->
      ${buildAdvanceBreakup(payments, advanceData)}

      <!-- FINE PAYMENT SECTION (only if fine exists) -->
      ${buildFineSection(fineData)}

      ${data.description ? `
        <div style="border: 3px solid #000000; padding: 8px 12px; margin-bottom: 8px; font-size: 12px; background: #ffffff; font-weight: 900; color: #000000;">
          <strong style="font-weight: 900;">Description:</strong> ${data.description}
        </div>
      ` : ""}
      
      <!-- SIGNATURES -->
      <div style="display: flex; justify-content: space-between; border-top: 3px solid #000000; padding-top: 12px; margin-top: 12px; font-size: 12px; color: #000000; font-weight: bold;">
        <div style="width: 45%;">
          <div style="margin-bottom: 40px; font-weight: 900;">Customer's Signature</div>
          <div style="border-bottom: 3px solid #000000;"></div>
        </div>
        <div style="width: 45%; text-align: right;">
          <div style="margin-bottom: 40px; font-weight: 900;">For Aurum Silver</div>
          <div style="border-bottom: 3px solid #000000;"></div>
        </div>
      </div>
      
      <!-- FOOTER -->
      <div style="text-align: center; border-top: 4px solid #000000; padding-top: 10px; margin-top: 12px; font-weight: 900; font-size: 14px; letter-spacing: 3px; color: #000000;">
        ✦ THANK YOU! VISIT AGAIN ✦
      </div>
      
    </div>
  `;
};

// ============================================================
// MAIN FUNCTION: Decides which bill to generate
// ============================================================
export const generateBillHTML = (billData) => {
  const { type, data, items, payments, customer, advanceData } = billData;

  if (type === "gst") {
    return generateGSTInvoice(data, items, payments, customer, advanceData);
  }

  return generateCashMemo(data, items, payments, customer, advanceData);
};

// Get CSS styles for printing - high contrast B&W
export const getBillStyles = () => `
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  body * {
    visibility: hidden !important;
  }
  
  #billContent {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 15px !important;
    background: #ffffff !important;
    color: #000000 !important;
    visibility: visible !important;
    border: 4px solid #000000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  #billContent * {
    visibility: visible !important;
    color: #000000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  #billContent table,
  #billContent th,
  #billContent td {
    border-color: #000000 !important;
  }
  
  #billContent th {
    font-weight: 900 !important;
    background: #ffffff !important;
    color: #000000 !important;
  }
  
  #billContent td {
    font-weight: bold !important;
  }
  
  table {
    page-break-inside: avoid !important;
  }
  
  @page {
    size: A4 portrait;
    margin: 5mm;
  }
}
`;

const billUtils = {
  formatDate,
  generateTransactionBillHTML,
  parseItemObject,
  getCarat,
  numberToWords,
  generateBillHTML,
  getBillStyles,
};

export default billUtils;